/**
 * Decides which admin app serves a given sidebar entry.
 *
 * The sidebar is built at runtime from the backend `navigation` response, and
 * its `url_path` values are shared by both admin apps — the backend has no way
 * to express "this screen now lives in v2". So the frontend owns that mapping:
 * anything not listed here is prefixed with the legacy admin host, which is why
 * a screen can be fully ported, merged and deployed and still open the old app.
 *
 * Adding a path below is the deliberate act of cutting that screen over.
 */

/**
 * Paths this app serves itself. Everything else goes to the legacy admin.
 *
 * This is an explicit list rather than a lookup against the v2 router, for two
 * reasons that both matter:
 *
 * 1. The router registers a catch-all (`/:pathMatch(.*)*`), so resolving a path
 *    against it matches *everything* — the check would send the whole menu to
 *    v2.
 * 2. Several routes are registered but still dark-launched (`/tracking`,
 *    `/delivery-box`, `/order-barcode`, `/generate-drawing-sheet`,
 *    `/buying-groups-sales-reps`). A router-aware check would cut all of them
 *    over at once, without anyone reviewing them.
 */
export const V2_OWNED_PATHS: readonly string[] = [
  '/default-categories',
  '/resellers-management',
  // staging-only: this branch's sidebar already treated /ai-analytics as in-app
  // (AiAnalyticsReport.vue landed here in b10d681). Kept through the master promotion
  // so this merge changes only what master changed, not where an unrelated menu item points.
  // NOTE: no /ai-analytics route is registered on any branch, so it currently
  // falls through the catch-all to /dashboard - a pre-existing gap.
  '/ai-analytics',
  // Settings pilot (AB#20755) — screens ported in PR 15329.
  '/general-settings',
  '/email-configuration',
  '/ui-branding',
  // Price List, SKU & Factories (AB#20756) — screens ported in PR 15682.
  '/price-list',
  '/factories',
  '/sku',
  // Categories, Design Categories & Brand (AB#20757) — screens ported in PR 15822.
  '/brands',
  '/design-categories',
  '/products/categories',
  // Translations, Email & Video Templates (AB#20758) — screens ported in PR 15849.
  '/translations',
  '/email-templates',
  '/video-templates',
  '/default-video-templates',
  '/video-usage',
  // Companies, Users, Roles & Permissions (AB#20754) — screens ported in PRs 15878, 15880, 15881.
  '/companies',
  '/users',
  '/roles',
  '/permissions',
  // Long-tail screens (AB#20759) and Customers (AB#21152) — ported in PRs 15931, 15933,
  // 15938, dark-launched on 12 Sep and cut over once each was exercised on the portal.
  '/general-action',
  '/products/data-source',
  '/customers',
];

/**
 * Whether this app serves the path itself.
 *
 * Every place that can send the user to a screen — the sidebar, the Quick Access
 * menu, any future shortcut — has to ask this same question. A link that decides on
 * its own is how a fully ported screen keeps opening the legacy app: the sidebar
 * lands you on the v2 page while the shortcut next to it bounces you back to v1.
 */
export const isV2OwnedPath = (path: string | null): path is string =>
  path !== null && V2_OWNED_PATHS.includes(path);

/**
 * Resolve a backend-supplied menu path to a href.
 *
 * Owned paths return the bare path, so vue-router handles them in this app.
 * Everything else is prefixed with the legacy host, preserving the previous
 * behaviour exactly — including for a null `url_path`, which the backend model
 * allows and which previously produced `${legacyBaseUrl}null`.
 */
export const resolveMenuHref = (path: string | null, legacyBaseUrl: string): string =>
  isV2OwnedPath(path) ? path : legacyBaseUrl + path;
